<script setup lang="ts">
/**
 * The chosen class's hulls as kit tiles and the chosen hull's catalogue render
 * big, leaning toward the pointer, beside three stat bars (hull points, speed,
 * guns) read from `@shared/ship-spec` and the catalogue's own overrides
 * (`landing-content.ts`). The hull is the parent's (`v-model:hull-id`).
 */
import { computed, ref } from 'vue';

import { useI18n } from '@/i18n';
import GameIcon from '@/ui/kit/GameIcon.vue';

import {
  FLEET_CLASSES,
  STAT_CEILING,
  type HullEntry,
  type ShipClass,
  hullStats,
  hullsOfClass,
  tierNumeral,
} from '../landing-content';
import { tiltFor } from '../tilt';
import StatBar from './StatBar.vue';

const props = defineProps<{ shipClass: ShipClass; hullId: string }>();
defineEmits<{ 'update:hullId': [id: string] }>();
const { t } = useI18n();

const hulls = computed(() => hullsOfClass(props.shipClass));
const hull = computed<HullEntry>(() => hulls.value.find((h) => h.id === props.hullId) ?? hulls.value[hulls.value.length - 1]!);
const stats = computed(() => hullStats(hull.value));
const blurb = computed(() => t(FLEET_CLASSES.find((c) => c.id === props.shipClass)!.blurbKey));
const fmt = (n: number): string => n.toLocaleString('en-GB');

const tilt = ref({ rx: 0, ry: 0 });
function onTilt(ev: PointerEvent): void {
  tilt.value = tiltFor(ev.clientX, ev.clientY, (ev.currentTarget as HTMLElement).getBoundingClientRect());
}
</script>

<template>
  <div class="hangar k-panel">
    <div class="stage k-well" @pointermove="onTilt" @pointerleave="tilt = { rx: 0, ry: 0 }">
      <div class="lean" :style="{ '--rx': `${tilt.rx}deg`, '--ry': `${tilt.ry}deg` }">
        <img :key="hull.id" class="render" :src="hull.render" :alt="t(hull.nameKey)" width="512" height="512" decoding="async" />
      </div>
      <span class="k-chip tier"><GameIcon name="star" :size="28" />{{ t('landing.fleet.tier', { n: tierNumeral(hull.tier) }) }}</span>
      <h3 :key="`n-${hull.id}`" class="k-title hull-name">{{ t(hull.nameKey) }}</h3>
    </div>

    <div class="side">
      <p class="k-label blurb">{{ blurb }}</p>
      <div class="bars">
        <StatBar icon="shield" :label="t('landing.fleet.stat.hp')" :value="fmt(stats.hp)" :ratio="stats.hp / STAT_CEILING.hp" />
        <StatBar icon="propeller" :label="t('landing.fleet.stat.speed')" :value="`${stats.speedKn} ${t('landing.fleet.knots')}`" :ratio="stats.speedKn / STAT_CEILING.speedKn" />
        <StatBar icon="cannons" :label="t('landing.fleet.stat.guns')" :value="`${stats.mounts} ×`" :ratio="stats.salvo / STAT_CEILING.salvo" />
      </div>
      <ul class="tiles">
        <li v-for="h in hulls" :key="h.id">
          <button type="button" class="tile" :class="{ on: h.id === hull.id }" :aria-pressed="h.id === hull.id" :aria-label="t(h.nameKey)" @click="$emit('update:hullId', h.id)">
            <img :src="h.render" alt="" width="96" height="96" loading="lazy" decoding="async" />
            <span class="k-chip pip">{{ tierNumeral(h.tier) }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.hangar {
  display: grid;
  gap: clamp(14px, 2.4vw, 28px);
  max-width: 1080px;
  margin: 0 auto;
  filter: drop-shadow(0 18px 30px rgba(0, 0, 0, 0.55));
}

.stage {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 260px;
  overflow: hidden;
  perspective: 900px;
}

.lean {
  transform: rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
  transition: transform 220ms var(--ease);
}

.render {
  display: block;
  width: min(100%, 440px);
  height: auto;
  aspect-ratio: 1;
  object-fit: cover;
  /* The catalogue renders sit on a near-black ground; fading the edge lets it melt into the well. */
  mask-image: radial-gradient(closest-side, #000 60%, transparent 100%);
  animation: hull-in 480ms var(--ease) both;
}

@keyframes hull-in {
  from {
    opacity: 0;
    transform: translateX(46px) scale(0.94);
  }
}

.tier {
  position: absolute;
  top: 10px;
  left: 10px;
}

.hull-name {
  position: absolute;
  left: 14px;
  bottom: 10px;
  font-size: clamp(1.4rem, 1rem + 1.6vw, 2.2rem);
  text-transform: uppercase;
  animation: hull-in 480ms var(--ease) both;
}

.side {
  display: grid;
  align-content: start;
  gap: 16px;
}

.blurb {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  -webkit-text-stroke: 0;
  text-shadow: none;
  color: #cfdbe6;
}

.bars {
  display: grid;
  gap: 12px;
}

.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(64px, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tile {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1;
  min-width: 56px;
  padding: 0;
  border: 0;
  border-radius: 12px;
  overflow: hidden;
  background: #0d141c;
  box-shadow: 0 0 0 2px #2c3b4c, 0 3px 0 2px #0d141c;
  touch-action: manipulation;
  transition: transform 180ms var(--ease);
}

.tile.on {
  box-shadow: 0 0 0 3px var(--kit-gold), 0 3px 0 3px #0d141c;
  transform: translateY(-3px);
}

.tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pip {
  position: absolute;
  right: 4px;
  bottom: 4px;
  padding: 0.05rem 0.45rem;
  font-size: 0.8rem;
}

@media (min-width: 900px) {
  .hangar {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  }

  .stage {
    min-height: 420px;
  }
}
</style>
